import * as RadixToast from "@radix-ui/react-toast";
import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";

import { nl } from "../i18n/nl";
import { CheckCircleIcon } from "./icons";
import styles from "./Toast.module.css";

export const TOAST_DURATION_MS = 5500;

export interface ToastOptions {
  message: string;
  /** Shows "Ongedaan maken"; every change gets an undo instead of a confirmation dialog. */
  onUndo?: () => void;
  duration?: number;
}

interface ToastItem extends ToastOptions {
  id: number;
}

type ShowToast = (options: ToastOptions) => void;

const ToastContext = createContext<ShowToast | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  const remove = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const show = useCallback<ShowToast>((options) => {
    const id = nextId.current++;
    // One toast at a time: a new action replaces the previous message.
    setToasts([{ ...options, id }]);
  }, []);

  const value = useMemo(() => show, [show]);

  return (
    <ToastContext.Provider value={value}>
      <RadixToast.Provider duration={TOAST_DURATION_MS} label={nl.common.close}>
        {children}
        {toasts.map((toast) => (
          <RadixToast.Root
            key={toast.id}
            type="background"
            duration={toast.duration ?? TOAST_DURATION_MS}
            className={styles.toast}
            onOpenChange={(open) => {
              if (!open) remove(toast.id);
            }}
          >
            <CheckCircleIcon />
            <RadixToast.Description className={styles.message}>{toast.message}</RadixToast.Description>
            {toast.onUndo && (
              <RadixToast.Action
                altText={nl.common.undo}
                className={styles.undo}
                onClick={() => {
                  toast.onUndo?.();
                }}
              >
                {nl.common.undo}
              </RadixToast.Action>
            )}
          </RadixToast.Root>
        ))}
        <RadixToast.Viewport className={styles.viewport} />
      </RadixToast.Provider>
    </ToastContext.Provider>
  );
}

export function useToast(): ShowToast {
  const show = useContext(ToastContext);
  if (!show) throw new Error("useToast must be used inside <ToastProvider>");
  return show;
}
